import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Briefcase, CheckCircle2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { routes } from '@/shared/constants'
import { AdditionalBusinessWizard } from '@/features/businesses/components/create-wizard/AdditionalBusinessWizard'
import { useMyOrganization } from './hooks/useMyOrganization'

/**
 * `/app/organization/new` — crear organización **y** primer negocio en una
 * mutación atómica (`portalCreateBusinessWithOrg`).
 *
 * Si el usuario ya tiene organización, NO redirige: muestra un panel informativo
 * con CTA para crear un nuevo negocio. El usuario decide.
 */
export function CreateOrganizationPage() {
  const navigate = useNavigate()
  const { organization, loading: orgLoading } = useMyOrganization()
  const goBack = () => navigate(routes.app.organization.detail)

  if (orgLoading && !organization) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-32 animate-pulse rounded bg-surface" />
        <Card>
          <div className="h-64 animate-pulse rounded bg-surface" />
        </Card>
      </div>
    )
  }

  if (organization) {
    return (
      <div className="space-y-6">
        <header>
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a Mi organización
          </button>
        </header>
        <Card className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success-soft text-success">
              <CheckCircle2 className="h-5 w-5" />
            </span>
            <div>
              <h1 className="font-serif text-2xl font-normal text-ink">
                Ya tienes una organización
              </h1>
              <p className="text-sm text-ink-muted">
                {organization.legalName ?? 'Tu organización'} ya está creada en TecoPOS.
              </p>
            </div>
          </div>
          <p className="text-sm text-ink-muted">
            ¿Quieres añadir un nuevo negocio a tu organización?
          </p>
          <div className="flex flex-wrap gap-2">
            {organization.canCreateBusiness ? (
              <Button onClick={() => navigate(routes.app.businesses.new)} variant="primary">
                <Briefcase className="h-4 w-4" />
                Crear negocio
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button variant="primary" disabled>
                      <Briefcase className="h-4 w-4" />
                      Crear negocio
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Paga la factura pendiente para poder crear más negocios.
                </TooltipContent>
              </Tooltip>
            )}
            <Button onClick={goBack} variant="outline">
              Volver a Mi organización
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header>
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a Mi organización
        </button>
        <h1 className="mt-2 font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">
          Crear mi organización
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          Define los datos para empezar a operar en TecoPOS. Crearemos tu organización y su
          primer negocio.
        </p>
      </header>

      <Card>
        <AdditionalBusinessWizard onCancel={goBack} firstTime />
      </Card>
    </div>
  )
}
